import { AMLS_COMMON } from './globalConstants';

const TOKEN_ID = (localStorage && localStorage.getItem('id_token')) || '';

export const COMMON_CONSTANTS = {
  BASE_URL: AMLS_COMMON,
  HTTP_HEADERS: Object.freeze({
    'Content-Type': 'application/json',
    'X-HTTP-Method-Override': 'PUT',
    // Authorization: `${TOKEN_ID}`
    Authorization: `Bearer ${TOKEN_ID}`
  }),
  GET_SUCCESS_DATA: 'getSuccessData',
  GET_FAILURE_DATA: 'getFailData',
  GET_DATA: 'getData'
};
export const DEFAULT_CURRENT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 10;
export const FRAUD_PREVENTION_SOURCE_IDS = [25, 29];

// FIN-13233 — VISIBILITY vs ENGINE. These two sets answer different questions and the
// backend keeps them deliberately distinct (see AlertSource.scala:210-226):
//
//   FRAUD_PREVENTION_SOURCE_IDS        [25, 29]      mirrors fraudPreventionProductionSources
//     "may this alert be shown on customer surfaces?" — EXCLUDES run-instance (24) because
//     run-instance alerts are evaluation artefacts that must not leak into customer detail,
//     bulk assign/status or case creation (FIN-12942, FIN-13060, FIN-13063/64/65).
//
//   FRAUD_PREVENTION_ENGINE_SOURCE_IDS [24, 25, 29]  mirrors fraudPreventionSources
//     "which engine produced this alert?" — INCLUDES 24, because a run-instance alert comes
//     off the same fraud engine and therefore carries triggered_rules, not triggered_typologies.
//
// Use the ENGINE set for payload-shape decisions (which column holds the names) and the
// VISIBILITY set for access/scoping decisions. They are not interchangeable.
export const FRAUD_PREVENTION_ENGINE_SOURCE_IDS = [24, 25, 29];

export const UNIFIED_ALERT_METRICS = {
  ALERT_RAISED: 'alertraised',
  SARS: 'sars',
  OPEN: 'open',
  CLOSED: 'closed',
  UNDER_INVESTIGATION: 'underinvestigation',
  ASSIGNED: 'assigned',
  PENDING_APPROVAL: 'pendingapproval',
  ALERT_COUNT: 'alertcount',
  ANOMALY_COUNT: 'anomalycount',
  COUNT_FOR_PAGINATION: 'countforpagination'
};

/**
 * Numeric AlertSource module IDs.
 * Mirrors `be/util/src/main/scala/com/tookitaki/amls/common/enums/AlertSource.scala`.
 * Used on pages whose alert-context API does not return `alertSourceId`, so the FE must
 * supply the moduleId explicitly (otherwise `TransactionDetails` falls back to the user's
 * `currentProductLogin.moduleId` which is unrelated to the alert).
 */
export const ALERT_SOURCE_MODULE_IDS = {
  MANUAL_CASE: 15
};

// AlertSource RealtimeFraudIadProduction ("Fraud Prevention") — matches BE
// DefaultFramlSourceId. Used by the FRAML View queue download payload.
export const FRAML_QUEUE_EXPORT_SOURCE_ID = 25;

export const TRANSACTION_HISTORY_MODULES = {
  // TM / CRS direct-login users
  TM_TRANSACTION_HISTORY: 'TM_TRANSACTION_HISTORY',
  CRS_TRANSACTION_HISTORY: 'CRS_TRANSACTION_HISTORY',

  // CM user viewing a standalone alert
  CM_TM_TRANSACTION_HISTORY: 'CM_TM_TRANSACTION_HISTORY',
  CM_CRS_TRANSACTION_HISTORY: 'CM_CRS_TRANSACTION_HISTORY',

  // CM user inside a case (alert tab)
  CM_CASE_TM_TRANSACTION_HISTORY: 'CM_CASE_TM_TRANSACTION_HISTORY',
  CM_CASE_CRS_TRANSACTION_HISTORY: 'CM_CASE_CRS_TRANSACTION_HISTORY',

  // CM user on take-action of an automated case (cross-alert)
  CM_CASE_TRANSACTION_HISTORY: 'CM_CASE_TRANSACTION_HISTORY',

  // CM user in a manual case
  CM_MANUAL_CUSTOMER_TRANSACTION_HISTORY:
    'CM_MANUAL_CUSTOMER_TRANSACTION_HISTORY',

  // FRAML-P (Fraud Prevention) alert detail
  FRAML_ALERT_TRANSACTION_HISTORY: 'FRAML_ALERT_TRANSACTION_HISTORY',
  CM_FRAML_ALERT_TRANSACTION_HISTORY: 'CM_FRAML_ALERT_TRANSACTION_HISTORY',
  CM_CASE_FRAML_ALERT_TRANSACTION_HISTORY:
    'CM_CASE_FRAML_ALERT_TRANSACTION_HISTORY',
  FRAML_CUSTOMER_QUEUE_EXPORT: 'FRAML_CUSTOMER_QUEUE_EXPORT',
  // FIN-12936 — FRAML customer-detail Transaction Details tab (customer-scoped)
  FRAML_CUSTOMER_TRANSACTION_HISTORY: 'FRAML_CUSTOMER_TRANSACTION_HISTORY'
};

/**
 * FIN-12835 — Activity export modules. All four already exist BE-side
 * (AsyncDownloadModule.scala: TmActivity / CmTmActivity / CmCaseTmActivity /
 * CmManualCustomerActivity, persisted UpperSnakecase), but the FE had no activity entry at all.
 * The notification click handler gated every navigation branch on
 * `module.includes('_history')`, and none of these contain '_history', so clicking an Activity
 * "Download All Matching" notification silently did nothing.
 */
export const ACTIVITY_DOWNLOAD_MODULES = {
  TM_ACTIVITY: 'TM_ACTIVITY',
  CM_TM_ACTIVITY: 'CM_TM_ACTIVITY',
  CM_CASE_TM_ACTIVITY: 'CM_CASE_TM_ACTIVITY',
  CM_MANUAL_CUSTOMER_ACTIVITY: 'CM_MANUAL_CUSTOMER_ACTIVITY',
  // FIN-13392 — FRAML customer-detail Activity Events tab (customer-scoped, CM surface).
  // Without its own module this export persisted under TM_ACTIVITY, whose notification
  // click routes by module alone (Topbar/notifications/dropdown.js) to fraudadmin's
  // run-instance surface or, with no alertId, a bare no-op — never the customer's own
  // Activity Details tab.
  FRAML_CUSTOMER_ACTIVITY: 'FRAML_CUSTOMER_ACTIVITY'
};

/**
 * FIN-13618 — the Activity module that belongs with an alert-detail transaction module, per the
 * pairs in AsyncDownloadModule.relatedModules (case / manual case / standalone CM). The alert
 * Activity tab used to send no module, so a CM export persisted under TM_ACTIVITY and its
 * notification routed to the TRM run-instance page — a 404 for CM-only users.
 * Returns undefined outside CM so ActivityInformation keeps its 'tm-activity' default.
 */
export const resolveActivityDownloadModule = (transactionModule = '') => {
  if (transactionModule.startsWith('CM_CASE_'))
    return ACTIVITY_DOWNLOAD_MODULES.CM_CASE_TM_ACTIVITY;
  if (transactionModule.startsWith('CM_MANUAL_'))
    return ACTIVITY_DOWNLOAD_MODULES.CM_MANUAL_CUSTOMER_ACTIVITY;
  if (transactionModule.startsWith('CM_'))
    return ACTIVITY_DOWNLOAD_MODULES.CM_TM_ACTIVITY;
  return undefined;
};

/**
 * Resolve the correct async-download module string from context.
 *
 * @param {boolean} isFromCM  - true when the user is accessing via Case Management
 * @param {boolean} isCaseAlert - true when the alert is viewed inside a CM case tab
 * @param {boolean} isCrsAlert  - true when the alert is a CRS-type alert
 */
export const resolveDownloadModule = ({
  isFromCM = false,
  isCaseAlert = false,
  isCrsAlert = false,
  isFramlP = false,
  isCustomerScope = false
} = {}) => {
  if (isFramlP) {
    // FIN-12936 — FRAML customer-detail page (customer-scoped, no single alert context)
    if (isCustomerScope)
      return TRANSACTION_HISTORY_MODULES.FRAML_CUSTOMER_TRANSACTION_HISTORY;
    if (isFromCM && isCaseAlert)
      return TRANSACTION_HISTORY_MODULES.CM_CASE_FRAML_ALERT_TRANSACTION_HISTORY;
    if (isFromCM)
      return TRANSACTION_HISTORY_MODULES.CM_FRAML_ALERT_TRANSACTION_HISTORY;
    return TRANSACTION_HISTORY_MODULES.FRAML_ALERT_TRANSACTION_HISTORY;
  }

  if (!isFromCM) {
    return isCrsAlert
      ? TRANSACTION_HISTORY_MODULES.CRS_TRANSACTION_HISTORY
      : TRANSACTION_HISTORY_MODULES.TM_TRANSACTION_HISTORY;
  }

  if (isCaseAlert) {
    return isCrsAlert
      ? TRANSACTION_HISTORY_MODULES.CM_CASE_CRS_TRANSACTION_HISTORY
      : TRANSACTION_HISTORY_MODULES.CM_CASE_TM_TRANSACTION_HISTORY;
  }

  return isCrsAlert
    ? TRANSACTION_HISTORY_MODULES.CM_CRS_TRANSACTION_HISTORY
    : TRANSACTION_HISTORY_MODULES.CM_TM_TRANSACTION_HISTORY;
};
